import classNames from "classnames"
import { Loader } from "common/Loader"
import { Table } from "common/Table/Table"
import { useRouter } from "hooks/useRouter"
import { formatPhone } from "pages/Contacts"
import { tabStyles } from "pages/GeneralData"
import { Fragment, useEffect, useState, useCallback } from "react"
import UserService from "services/user.service"
import { Modal } from "common/Modal"
import { useNotifications } from "hooks/useNotification"
import Select from "react-select"
import { useDebounce } from "hooks/useDebounce"
import swal from "sweetalert"

const service = new UserService()

export const SpecificBlitz = () => {
  const columns = [
    { key: "badge_id", dbColName: "badge_id", title: "badge id" },
    { key: "first_name", dbColName: "first_name", title: "first name" },
    { key: "last_name", dbColName: "last_name", title: "last name" },
    { key: "phone_number", dbColName: "phone_number", title: "phone number" },
    {
      key: "rep_start_date",
      dbColName: "rep_start_date",
      title: "rep start date",
    },
    { key: "rep_end_date", dbColName: "rep_end_date", title: "rep end date" },
    {
      key: "tenure_registered",
      dbColName: "tenure_registered",
      title: "tenure registered",
    },
    { key: "position", dbColName: "position", title: "position" },
    {
      key: "blitz_bonus_opt_in",
      dbColName: "blitz_bonus_opt_in",
      title: "blitz bonus opt in",
    },
    { key: "tenure", dbColName: "tenure", title: "tenure" },
    { key: "team", dbColName: "team", title: "team" },
    {
      key: "management_group",
      dbColName: "management_group",
      title: "management group",
    },
    {
      key: "senior_management_group",
      dbColName: "senior_management_group",
      title: "senior management group",
    },
    { key: "region", dbColName: "region", title: "region" },
    {
      key: "senior_region",
      dbColName: "senior_region",
      title: "senior region",
    },
    { key: "partnership", dbColName: "partnership", title: "partnership" },
  ]

  const uploadColumns = [
    { key: "badge_id", title: "badge_id", dbColName: "badge_id" },
    {
      key: "url",
      title: "url",
      dbColName: "url",
      render: (url) => (
        <a
          className="text-blue-500"
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          {url}
        </a>
      ),
    },
  ]

  const { params, push } = useRouter()

  const [loading, setLoading] = useState(false)
  const [unregUsers, setUnregUsers] = useState([])
  const [regUsers, setRegUsers] = useState([])
  const [data, setData] = useState<any>(null)
  const [tab, setTab] = useState("Reg")
  const [showAddRepModal, setShowAddRepModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    badge_id: "",
    rep_start_date: "",
    rep_end_date: "",
    blitz_bonus_opt_in: "Yes",
  })
  const [repSearchValue, setRepSearchValue] = useState("")
  const [reps, setReps] = useState([])
  const [loadingReps, setLoadingReps] = useState(false)
  const [selectedRep, setSelectedRep] = useState<any>(null)

  const { errorMessage, successMessage } = useNotifications()
  const debouncedRepSearch = useDebounce(repSearchValue, 500)

  useEffect(() => {
    setLoading(true)
    service
      .getBlitzDetails(params?.id)
      .then((resp) => {
        setData(resp.data.data)
        setUnregUsers(resp.data.data.unregistered_users)
        setRegUsers(resp.data.data.users)
        // Pre-populate form with blitz data
        setFormData({
          badge_id: "",
          rep_start_date: resp.data.data?.start_date || "",
          rep_end_date: resp.data.data?.end_date || "",
          blitz_bonus_opt_in: "Yes",
        })
        // setUploads(resp.data.data.uploads);
        // setTotalItems(resp.data.total_docs);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setLoading(false)
      })
  }, [params?.id])

  const fetchReps = useCallback(() => {
    if (showAddRepModal) {
      setLoadingReps(true)
      service
        .getSignedReps({
          repName: debouncedRepSearch || "",
          pageNumber: 1,
          year: "All",
          pageSize: 100,
        })
        .then((resp) => {
          if (resp.data.success) {
            setReps(
              resp.data.data.map((item: any) => ({
                value: item.rep_id,
                label: `${item.rep_name} - ${item.badge_id || item.rep_id}`,
              }))
            )
          }
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          setLoadingReps(false)
        })
    }
  }, [debouncedRepSearch, showAddRepModal])

  useEffect(() => {
    fetchReps()
  }, [fetchReps])

  const handleAddRep = () => {
    if (!data) {
      errorMessage("Blitz data not loaded")
      return
    }
    setShowAddRepModal(true)
    // Reset rep selection when opening modal
    setSelectedRep(null)
    setRepSearchValue("")
    setFormData({
      badge_id: "",
      rep_start_date: data?.start_date || "",
      rep_end_date: data?.end_date || "",
      blitz_bonus_opt_in: "Yes",
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedRep || !formData.badge_id) {
      errorMessage("Please select a rep")
      return
    }

    if (!formData.rep_start_date) {
      errorMessage("Please enter a start date")
      return
    }

    if (!formData.rep_end_date) {
      errorMessage("Please enter an end date")
      return
    }

    setSubmitting(true)

    const payload = {
      badge_id: formData.badge_id,
      blitz_id: data._id,
      rep_end_date: formData.rep_end_date,
      rep_start_date: formData.rep_start_date,
      blitz_bonus_opt_in: formData.blitz_bonus_opt_in,
    }

    try {
      const resp = await service.assignSignedRepToBlitz(payload)
      if (resp.data.success) {
        successMessage("Rep assigned to blitz successfully")
        setShowAddRepModal(false)
        // Refresh the data
        const refreshResp = await service.getBlitzDetails(params?.id)
        setData(refreshResp.data.data)
        setUnregUsers(refreshResp.data.data.unregistered_users)
        setRegUsers(refreshResp.data.data.users)
        // Reset form
        setFormData({
          badge_id: "",
          rep_start_date: data?.start_date || "",
          rep_end_date: data?.end_date || "",
          blitz_bonus_opt_in: "Yes",
        })
        setSelectedRep(null)
        setRepSearchValue("")
      } else {
        errorMessage(resp.data.message || "Failed to assign rep to blitz")
      }
    } catch (error: any) {
      console.error(error)
      errorMessage(
        error?.response?.data?.message || "Failed to assign rep to blitz"
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteUser = (badgeId: string) => {
    swal({
      title: "Are you sure, you want to delete this user?",
      icon: "warning",
      className: "w-[500px]",
      buttons: {
        cancel: {
          text: "Cancel",
          visible: true,
        },
        confirm: {
          text: "Delete",
          className: "bg-primary text-white",
        },
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        service
          .deleteBlitzUser(params?.id, badgeId)
          .then((res) => {
            successMessage("User deleted successfully")
            // Refresh the data
            service
              .getBlitzDetails(params?.id)
              .then((resp) => {
                setData(resp.data.data)
                setUnregUsers(resp.data.data.unregistered_users)
                setRegUsers(resp.data.data.users)
              })
              .catch((err) => console.error(err))
          })
          .catch((err) => {
            console.error(err)
            errorMessage("Failed to delete user")
          })
      }
    })
  }

  if (loading) {
    return <Loader />
  }

  return (
    <Fragment>
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3 mb-4">
          <button
            onClick={() => push("/blitz")}
            className="btn-secondary"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
              />
            </svg>
          </button>
          <div>
            <h1 className="font-medium text-gray-700 tracking-wide text-lg">
              Blitz Users
            </h1>
            <p className="text-gray-500 text-sm leading-none">View all users</p>
          </div>
        </div>
        <button
          onClick={handleAddRep}
          className="btn-dark"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Rep
        </button>
      </div>

      <div className="flex gap-4">
        <div className="w-1/3">
          <div className="flex gap-3">
            <div className="flex-1 items-center justify-center gap-2 border border-zinc-200 rounded-lg bg-black p-2 flex">
              <div className="h-5 w-5">
                <img
                  alt="person"
                  src="/person_icon.png"
                  className="h-full w-full mx-auto"
                />
              </div>
              <p className="text-lg text-red-500">{data?.sales_counts}</p>
            </div>
            <div className="flex-1 items-center justify-center gap-2 border border-zinc-200 rounded-lg bg-black p-2 flex">
              <div className="h-5 w-5">
                <img
                  alt="person"
                  src="/rep_icon.png"
                  className="h-full w-full mx-auto"
                />
              </div>
              <p className="text-lg text-red-500">{data?.tech_counts}</p>
            </div>
            <div className="flex-1 items-center justify-center gap-2 border border-zinc-200 rounded-lg bg-black p-2 flex">
              <div className="h-5 w-5">
                <img
                  alt="person"
                  src="/rep_person.png"
                  className="h-full w-full mx-auto"
                />
              </div>
              <p className="text-lg text-red-500">{data?.solar_counts}</p>
            </div>
          </div>
          <p className="mt-4 text-lg font-semibold">Blitz Details</p>

          <div className="flex flex-col gap-2">
            <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
              <div className="flex justify-between gap-4 py-3">
                <span>Blitz Creator</span>
                <span>{data?.first_name + " " + data?.last_name}</span>
              </div>
              <div className="flex justify-between gap-4 py-3">
                <span>Blitz Key</span>
                <span>{data?.blitz_key}</span>
              </div>
              {/* <div className="flex justify-between gap-4 py-3">
                <span>Blitz Link</span>
                <span>{data?.trainer_name}</span>
              </div> */}
              <div className="flex justify-between gap-4 py-3">
                <span>Blitz Creation Date</span>
                <span>{new Date(data?.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
            <div className="flex justify-between gap-4 py-3">
              <span>Sales Office</span>
              <span>{data?.sale_office}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Start Date</span>
              <span>{data?.start_date}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>End Date</span>
              <span>{data?.end_date}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>State</span>
              <span>{data?.state}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Zip Code</span>
              <span>{data?.zip_code}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Blitz Month</span>
              <span>{data?.month_of_blitz}</span>
            </div>
          </div>
          <div className="card mt-1.5 flex flex-col px-4 py-2 divide-y divide-line">
            <div className="flex justify-between gap-4 py-3">
              <span>Estimated Rookies</span>
              <span>{data?.rookies}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Estimate Reps</span>
              <span>{data?.vets}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Estimate FP</span>
              <span>{data?.est_accounts}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Techs Requested</span>
              <span>{data?.techs_needed}</span>
            </div>
            <div className="flex justify-between gap-4 py-3">
              <span>Solar Closer Request</span>
              <span>{data?.request_solar_closer}</span>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="tabs mb-4">
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => setTab("Reg")}
                className={classNames(
                  `${tabStyles.default} flex items-center justify-center gap-2`,
                  {
                    [tabStyles.active]: tab === "Reg",
                  }
                )}
              >
                <span>Registered Blitz</span>
                <span className="bg-gray-200 px-2 py-1 rounded-full text-xs flex-shrink-0">
                  {regUsers?.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setTab("Unreg")}
                className={classNames(
                  `${tabStyles.default} flex items-center justify-center gap-2`,
                  {
                    [tabStyles.active]: tab === "Unreg",
                  }
                )}
              >
                <span> Unregistered Blitz</span>
                <span className="bg-gray-200 px-2 py-1 rounded-full text-xs flex-shrink-0">
                  {unregUsers?.length}
                </span>
              </button>
            </div>
          </div>

          {tab === "Reg" ? (
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
              <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">
                      Name
                    </th>

                    <th scope="col" className="px-6 py-3">
                      Tenure
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Badge ID
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Start Date
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Phone
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {regUsers?.map((item: any, index) => (
                    <tr key={index} className="bg-white border-b">
                      <th
                        scope="row"
                        className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap"
                      >
                        {item?.first_name + " " + item?.last_name}
                      </th>

                      <td className="px-6 py-4">{item?.tenure}</td>
                      <td className="px-6 py-4">{item?.badge_id}</td>

                      <td className="px-6 py-4">{item?.rep_start_date}</td>

                      <td className="px-6 py-4">
                        {formatPhone(item?.phone_number)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDeleteUser(item.badge_id)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
              <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">
                      Name
                    </th>

                    <th scope="col" className="px-6 py-3">
                      Tenure
                    </th>
                    <th scope="col" className="px-6 py-3">
                      Badge ID
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {unregUsers.map((item: any, index) => (
                    <tr
                      key={index}
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                    >
                      <th
                        scope="row"
                        className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                      >
                        {item?.first_name + " " + item.last_name}
                      </th>
                      <td className="px-6 py-4">{item?.tenure_registered}</td>
                      <td className="px-6 py-4">{item?.badge_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        showModal={showAddRepModal}
        setShowModal={setShowAddRepModal}
        title="Add Rep to Blitz"
        width="w-full md:w-1/2"
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="field-label">
              Rep Name <span className="text-red-500">*</span>
            </label>
            <Select
              options={reps}
              placeholder="Search rep by name..."
              value={selectedRep}
              onChange={(selected: any) => {
                setSelectedRep(selected)
                setFormData({
                  ...formData,
                  badge_id: selected?.value || "",
                })
              }}
              isLoading={loadingReps}
              onInputChange={(newValue) => setRepSearchValue(newValue)}
              className="react-select"
              classNamePrefix="react-select"
              isClearable
              isSearchable
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="field-label">
                Rep Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.rep_start_date}
                onChange={(e) =>
                  setFormData({ ...formData, rep_start_date: e.target.value })
                }
                required
                className="w-full px-3 py-2 text-sm"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="field-label">
                Rep End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.rep_end_date}
                onChange={(e) =>
                  setFormData({ ...formData, rep_end_date: e.target.value })
                }
                required
                className="w-full px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="field-label">
              Blitz Bonus Opt In <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.blitz_bonus_opt_in}
              onChange={(e) =>
                setFormData({ ...formData, blitz_bonus_opt_in: e.target.value })
              }
              required
              className="w-full px-3 py-2 text-sm"
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="flex gap-3 justify-end mt-4">
            <button
              type="button"
              onClick={() => setShowAddRepModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-white bg-ink hover:bg-gray-800 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting..." : "Add Rep"}
            </button>
          </div>
        </form>
      </Modal>

      {/* {tab === "Users" ? (
        <Table
          data={data}
          loadingData={loading}
          setPagination={setPagination}
          pagination={pagination}
          totalItems={totalItems}
          columns={columns}
        />
      ) : (
        <Table
          data={uploads}
          loadingData={loading}
          setPagination={setPagination}
          pagination={pagination}
          totalItems={totalItems}
          columns={uploadColumns}
        />
      )}
      <div className="tabs mt-4">
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setTab("Users")}
            className={classNames(tabStyles.default, {
              [tabStyles.active]: tab === "Users",
            })}
          >
            Users
          </button>
          <button
            type="button"
            onClick={() => setTab("Uploads")}
            className={classNames(tabStyles.default, {
              [tabStyles.active]: tab === "Uploads",
            })}
          >
            Uploads
          </button>
        </div>
      </div> */}
    </Fragment>
  )
}
